import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const confirmBtn =
    tone === 'danger'
      ? 'bg-[#f5476a] hover:bg-[#e03e5f] text-white'
      : 'bg-[#4f8ff8] hover:opacity-90 text-white';

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 dark:bg-black/60 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-[#0e1624] border border-gray-200 dark:border-[#1d2d47] rounded-2xl max-w-sm w-full p-6 shadow-xl text-left"
        style={{ direction: 'ltr' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-3">
          {tone === 'danger' && (
            <div className="w-9 h-9 rounded-xl bg-red-50 dark:bg-[#f5476a]/10 border border-red-100 dark:border-[#f5476a]/20 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4 text-[#f5476a]" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 2h2M4 5h12M5 5l1 11a1 1 0 001 1h6a1 1 0 001-1l1-11" />
              </svg>
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-[#f1f5fb]">
              {title}
            </h3>
            {description && (
              <p className="text-[13px] text-gray-500 dark:text-[#a3b6cc] mt-1.5 leading-relaxed">
                {description}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2 pt-4 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-xs bg-gray-100 dark:bg-[#141e30] text-gray-600 dark:text-[#c2d0e0] font-semibold rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-xs font-semibold rounded-lg disabled:opacity-50 transition-opacity ${confirmBtn}`}
          >
            {loading ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
